import { useEffect, useState } from "react";
import { Video, ExternalLink } from "lucide-react";
import { fmtDue } from "@/components/dashboard/CourseWork";
import { PROVIDER_LABEL } from "@/components/school/SchoolConnect";

const tm = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" });
const isLive = (c) => { const n = Date.now(); return n >= new Date(c.start_at) - 10 * 60000 && n <= new Date(c.end_at); };
const isOver = (c) => Date.now() > new Date(c.end_at);

export const useMinuteTick = () => {
  const [, setT] = useState(0);
  useEffect(() => { const id = setInterval(() => setT((x) => x + 1), 30000); return () => clearInterval(id); }, []);
};

export const JoinButton = ({ c, small = false, where = "list" }) => {
  const url = c.join_url || c.link;
  if (!url || isOver(c)) return null;
  const live = isLive(c);
  const size = small ? "px-3 py-1 text-xs" : "px-4 py-2 text-sm";
  if (!live) return <span aria-disabled="true" title="Join opens 10 minutes before class" data-testid={`school-class-join-${where}-${c.uid}`}
    className={`inline-flex items-center gap-1.5 rounded-full whitespace-nowrap cursor-not-allowed bg-[#F1EFEA] text-stone-500 ${size}`}><Video className="w-3.5 h-3.5" />Opens {tm(new Date(new Date(c.start_at) - 10 * 60000).toISOString())}</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid={`school-class-join-${where}-${c.uid}`}
      className={`inline-flex items-center gap-1.5 rounded-full font-medium whitespace-nowrap bg-[#F28C38] text-[#3B1D05] hover:bg-[#E57D27] transition-colors ${size}`}>
      {c.join_url ? <Video className="w-3.5 h-3.5" /> : <ExternalLink className="w-3.5 h-3.5" />}Join now
    </a>
  );
};

export const ClassTime = ({ c }) => `${tm(c.start_at)} – ${tm(c.end_at)}`;

const ClassRow = ({ c }) => (
  <li className={`flex flex-wrap items-center gap-3 rounded-2xl px-4 py-3 border ${isLive(c) ? "border-[#F28C38] bg-[#FFF4EA]" : "border-[#EEE8DA] bg-[#FBF8F1]"} ${isOver(c) ? "opacity-60" : ""}`} data-testid={`school-class-${c.uid}`}>
    <span className="sm:w-44 whitespace-nowrap text-sm font-medium text-[#2C4A3E]"><ClassTime c={c} /></span>
    <span className="flex-1 min-w-[180px]"><b className="text-[#1F3D33]">{c.title}</b><span className="block text-xs text-stone-500">{PROVIDER_LABEL[c.provider || "other"]} · {c.course}</span></span>
    {isLive(c) && <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#C0571B] text-white text-[11px] font-semibold" data-testid={`school-class-live-${c.uid}`}><span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />LIVE NOW</span>}
    {isOver(c) && <span className="text-xs text-stone-500">Ended</span>}
    <JoinButton c={c} />
  </li>
);

const ClassDay = ({ label, cs, testId, empty }) => (
  <div data-testid={testId}>
    <p className="text-xs uppercase tracking-widest text-stone-500 mb-2">{label}</p>
    {cs.length ? <ul className="space-y-2">{cs.map((c) => <ClassRow key={c.uid} c={c} />)}</ul> : <p className="text-sm text-stone-500">{empty}</p>}
  </div>
);

export const SchoolClasses = ({ classes, today }) => {
  useMinuteTick();
  if (!classes?.length) return null;
  const todays = classes.filter((c) => c.date === today), later = classes.filter((c) => c.date > today);
  const byDay = later.reduce((m, c) => ({ ...m, [c.date]: [...(m[c.date] || []), c] }), {});
  return (
    <section className="mt-10 rounded-3xl bg-white border border-[#E6DEC3] p-6 md:p-8" data-testid="school-classes">
      <h2 className="font-display text-3xl text-[#2C4A3E] inline-flex items-center gap-2"><Video className="w-6 h-6 text-[#F28C38]" /> Live Classes</h2>
      <p className="text-sm text-stone-500 mt-1">From your school calendar. Join opens 10 minutes before class. Times are Eastern.</p>
      <div className="mt-4 space-y-4">
        <ClassDay label="Today" cs={todays} testId="school-classes-today" empty="No live classes today." />
        {Object.keys(byDay).length > 0 && <p className="text-xs uppercase tracking-widest text-[#C0571B] font-semibold pt-2" data-testid="school-classes-upcoming">Upcoming</p>}
        {Object.entries(byDay).map(([date, cs]) => <ClassDay key={date} label={fmtDue(date)} cs={cs} testId={`school-classes-day-${date}`} />)}
      </div>
    </section>
  );
};
